import React, { createContext, useContext, useState, useEffect } from "react";
import { jsPDF } from "jspdf"; // Import jsPDF for PDF generation
import io from "socket.io-client";
import QRCode from "qrcode";
import Logger from "../Component/Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

const socket = io(process.env.REACT_APP_SOCKET_URL);

// Initialize Logger
const logger = new Logger(getLoggerBaseURL());

// Create context to share modal state
const ModalContext = createContext();

export const useSocket = () => {
    return useContext(ModalContext);
};

export const SocketProvider = ({ children }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [queuedRequests, setQueuedRequests] = useState(
        JSON.parse(localStorage.getItem("queuedRequests")) || []
    );
    const [pdfGenerated, setPdfGenerated] = useState(false);
    const [buttonClicked, setButtonClicked] = useState(false);
    const [isModalClosed, setIsModalClosed] = useState(false);
    const summaryOrdersLength = sessionStorage.getItem('summaryOrdersLength');

    // Open the modal and pass data to it
    const openModal = React.useCallback((data) => {
        if (isModalClosed) return;

        setModalData(data);
        setIsModalOpen(true);
    }, [isModalClosed]);

    // Close the modal and mark it as permanently closed
    const closeModal = () => {
        setIsModalOpen(false);
        setModalData(null);
        setPdfGenerated(false);
        setButtonClicked(false);
        setIsModalClosed(true);
    };

    // Function to generate PDF
    const generatePDF = async (printMode = false) => {
        if (!modalData) return;
        const { key } = modalData;

        if (!key) {
            logger.log("Missing key value for PDF generation", "ERROR");
            return;
        }

        // Create PDF (ignores page size but limits content to 2x3 inches)
        const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });

        // Fixed 2x3-inch content area
        const contentWidth = 216; // 2 inches (72pt per inch)
        const topMargin = 20;

        try {
            const qrCodeDataURL = await QRCode.toDataURL(modalData.key);

            for (let i = 0; i < modalData.noOfCopies; i++) {
                if (i > 0) doc.addPage();

                // Set font and title
                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text("Order Details", contentWidth / 2, topMargin + 10, { align: "center" });

                // Set font for details
                doc.setFontSize(8);
                doc.setFont("helvetica", "normal");

                // Define positioning variables
                const textStartX = contentWidth - 130; // Move text details to the right
                const qrCodeX = 10; // Move QR code to the left
                const qrCodeSize = 70;
                const qrCodeY = topMargin + 20;
                let textStartY = topMargin + 35;
                let lineSpacing = 10;

                // Add QR Code (Now on the LEFT side)
                doc.addImage(qrCodeDataURL, "PNG", qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);

                // Add order details
                if (modalData.Summary_No) {
                    doc.text(`S.NO: ${modalData.Summary_No}`, textStartX, textStartY);
                    textStartY += lineSpacing;
                    doc.text(`No. of Orders: ${summaryOrdersLength}`, textStartX, textStartY);
                } else {
                    doc.text(`Order: ${modalData.Order_No}`, textStartX, textStartY);
                }
                textStartY += lineSpacing;
                doc.text(`Packets: ${modalData.noOfPackets}`, textStartX, textStartY);
                textStartY += lineSpacing;
                doc.text(`Boxes: ${modalData.noOfBoxes}`, textStartX, textStartY);
                textStartY += lineSpacing;
                doc.text(`Weight: ${modalData.weightOfOrder}`, textStartX, textStartY);
                textStartY += lineSpacing;
                doc.text(`Area: ${modalData.area}`, textStartX, textStartY);
            }

            logger.log(`PrintMode: ${printMode}`, "INFO");
            if (printMode) {
                setPdfGenerated(true);
                doc.autoPrint();
                window.open(doc.output("bloburl"), "_blank");
            } else {
                setPdfGenerated(true);
                setButtonClicked(true);
                doc.save("print-request.pdf");
            }
        } catch (error) {
            logger.log(`Error generating QR code or PDF: ${error}`, "ERROR");
        }
    };

    useEffect(() => {
        socket.on("connect", () => {
            logger.log(`Socket connected: ${socket.id}`, "INFO");

            queuedRequests.forEach((data) => {
                openModal(data);
                logger.log(`Processing queued request:: ${data}`, "INFO");
            });

            setQueuedRequests([]);
            localStorage.removeItem("queuedRequests");
        });

        // Function to validate admin email and open the modal
        const validateAndOpenModal = (data) => {
            const storedAdminEmail = sessionStorage.getItem("adminEmail")?.toLowerCase().trim();
            const dataAdminEmail = data.adminEmail?.toLowerCase().trim();

            logger.log(`Stored Admin Email:: ${storedAdminEmail}`, "INFO");
            logger.log(`Data Admin Email:: ${dataAdminEmail}`, "INFO");

            if (storedAdminEmail === dataAdminEmail) {
                logger.log("Emails match. Opening modal...", "INFO");
                openModal(data); // Ensure this function is called correctly
            } else {
                logger.log("Emails do not match. Modal will not open.", "ERROR");
            }
        };

        // Listen for print requests and validate email before opening modal
        socket.on("print-request", (data) => {
            logger.log(`Received print-request: ${data}`, "INFO");

            if (socket.connected) {
                validateAndOpenModal(data);
            } else {
                // Queue the request if socket isn't connected
                setQueuedRequests((prevQueue) => {
                    const newQueue = [...prevQueue, data];
                    localStorage.setItem("queuedRequests", JSON.stringify(newQueue));
                    logger.log(`Socket disconnected. Queuing request: ${data}`, "INFO");
                    return newQueue;
                });
            }
        });

        return () => {
            socket.off("print-request");
        };
    }, [queuedRequests, openModal]);

    return (
        <ModalContext.Provider value={{ isModalOpen, modalData, openModal, closeModal, generatePDF }}>
            {isModalOpen && (
                <div style={modalStyles.overlay}>
                    <div style={modalStyles.modalContent}>
                        <h2 style={modalStyles.heading}>Print Request</h2>
                        <p style={modalStyles.text}>Summary No: {modalData?.Summary_No || modalData?.Order_No}</p>
                        <p style={modalStyles.text}>Number of Copies: {modalData?.noOfCopies}</p>
                        <div style={modalStyles.buttonContainer}>
                            {!buttonClicked && (
                                <button onClick={() => generatePDF(false)} style={modalStyles.button}>
                                    Generate PDF
                                </button>
                            )}
                            <button onClick={() => generatePDF(true)} style={modalStyles.button}>
                                Print
                            </button>
                            {pdfGenerated && (
                                <button onClick={closeModal} style={modalStyles.closeButton}>
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {children}
        </ModalContext.Provider>
    );
};

const modalStyles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "5px",
        width: "400px",
        textAlign: "center",
    },
    heading: {
        marginBottom: "15px",
        color: "#333",
    },
    text: {
        fontSize: "14px",
        marginBottom: "10px",
        color: "#555",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        gap: "10px",
        marginTop: "20px",
    },
    button: {
        padding: "10px 15px",
        backgroundColor: "#79c6e9",
        color: "black",
        border: "none",
        cursor: "pointer",
        borderRadius: "5px",
        fontSize: "14px",
    },
    closeButton: {
        padding: "10px 15px",
        backgroundColor: "#d9534f",
        color: "white",
        border: "none",
        cursor: "pointer",
        borderRadius: "5px",
        fontSize: "14px",
    },
};
